import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkPermission } from '../utils/auth';
import useUserStore from '../store/user';
import { Toast } from '@douyinfe/semi-ui';

export interface UsePermissionReturn {
  /**
   * 检查是否有权限访问指定路径
   */
  hasPermission: (path: string) => boolean;

  /**
   * 检查权限并根据结果执行相应操作
   */
  checkAndNavigate: (path: string, onUnauthorized?: () => void) => boolean;

  /**
   * 显示权限检查结果的方法
   */
  showPermissionCheck: (path: string) => void;
}

/**
 * 权限检查自定义Hook
 * 提供统一的权限检查逻辑
 */
export const usePermission = (): UsePermissionReturn => {
  const navigate = useNavigate();
  const { userInfo } = useUserStore();

  /**
   * 检查是否有权限访问指定路径
   */
  const hasPermission = useCallback(
    (path: string): boolean => {
      const result = checkPermission(userInfo, path);
      return result.hasPermission;
    },
    [userInfo]
  );

  /**
   * 检查权限并根据结果执行相应操作
   */
  const checkAndNavigate = useCallback(
    (path: string, onUnauthorized?: () => void): boolean => {
      const result = checkPermission(userInfo, path);

      if (result.hasPermission) {
        return true;
      }

      // 处理未授权情况
      if (result.reason === 'unauthorized' && result.redirectTo) {
        if (onUnauthorized) {
          onUnauthorized();
        } else {
          // 显示提示信息
          Toast.warning('您没有权限访问该页面，请先完成授权');
          // 跳转到授权页面
          navigate(result.redirectTo);
        }
      }

      return false;
    },
    [userInfo, navigate]
  );

  /**
   * 显示权限检查结果的方法
   */
  const showPermissionCheck = useCallback(
    (path: string): void => {
      checkAndNavigate(path);
    },
    [checkAndNavigate]
  );

  return {
    hasPermission,
    checkAndNavigate,
    showPermissionCheck,
  };
};

export default usePermission;
